import { ReactElement } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteIcon from "@mui/icons-material/DeleteRounded";
import EditIcon from "@mui/icons-material/EditRounded";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { Chip, IconButton, Paper, Stack, Typography } from "@mui/material";

import { CourtDayType, CourtResponse } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { useTranslation } from "@/translations";

import { buildCourtAddress, buildCourtDayChips } from "./CourtCard.utils";

const chipIcons: Record<CourtDayType, ReactElement> = {
  [CourtDayType.Weekday]: <AccessTimeIcon fontSize="small" />,
  [CourtDayType.Rule]: <EventRepeatIcon fontSize="small" />,
  [CourtDayType.SingleDates]: <CalendarMonthIcon fontSize="small" />,
};

interface Props {
  court: CourtResponse;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

export function CourtCard({ court, onEdit, onDelete, isDeleting }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const chips = buildCourtDayChips(court.courtDays, t, language);
  const districts = [...court.districts].sort((a, b) => a - b).join(", ");
  const address = buildCourtAddress(court);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack flexDirection={"row"} gap={2} justifyContent={"space-between"}>
        <Stack gap={2}>
          <Stack flexDirection={"row"} gap={2} flexWrap={"wrap"} alignItems={"center"}>
            <Chip label={court.shortCode} color="primary" sx={{ fontWeight: 700, borderRadius: 1 }} />
            <Typography variant="h4" sx={{ mr: 1 }}>
              {court.name}
            </Typography>
            {chips.length === 0 ? (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                {t("settings.courts.card.noGerichtstage")}
              </Typography>
            ) : (
              chips.map((chip, index) => (
                <Chip key={index} icon={chipIcons[chip.kind]} label={chip.label} size="medium" />
              ))
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t("settings.courts.card.districts", { districts })}
          </Typography>
          {address && (
            <Typography variant="body2" color="text.secondary">
              {t("settings.courts.card.address", { address })}
            </Typography>
          )}
        </Stack>
        <Stack flexDirection={"row"} alignItems={"center"}>
          <IconButton onClick={onEdit} aria-label={t("settings.courts.card.editAria", { name: court.name })}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={onDelete}
            disabled={isDeleting}
            aria-label={t("settings.courts.card.deleteAria", { name: court.name })}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
}
